import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import ReactFlow, {
  Node, Edge, Background, Handle, Position, NodeProps, BackgroundVariant, MarkerType,
  ReactFlowProvider, useReactFlow, useStore,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { T, FONT, radius, shadow, tint } from './poyais-ui/theme';
import { Button, IconChip } from './poyais-ui/primitives';
import { Icon, Ico } from './poyais-ui/icons';
import { useViewport } from './poyais-ui/hooks';
import { useLang, addTranslations } from './i18n';
import PoyaisSpinner from './PoyaisSpinner';

addTranslations({
  'ROAS eşik değerin altında': 'ROAS below your threshold',
  'ROAS eşik değerin üstünde': 'ROAS above your threshold',
  'Zorunlu: ROAS için varsayılan değer yoktur.': 'Required: there is no default value for ROAS.',
  'Önce ROAS eşik değerini gir': 'Enter the ROAS threshold first',
  'Eşik girilmedi': 'No threshold set',
  'Örn.': 'e.g.',
  'Sıfır dönüşüm': 'Zero conversions',
  'CTR düşük': 'Low CTR',
  'Bütçe erken tüketildi': 'Budget spent early',
  'Organik trafik düştü': 'Organic traffic dropped',
  'Dönüşüm oranı düştü': 'Conversion rate dropped',
  'Zamanlanmış': 'Scheduled',
  'ROAS bu değerin altına düştüğünde tetiklenir': 'Fires when ROAS drops below this value',
  'ROAS bu değerin üstüne çıktığında tetiklenir': 'Fires when ROAS rises above this value',
  "Harcama 200'ü aşıp hiç dönüşüm gelmediğinde tetiklenir": 'Fires when spend passes 200 with no conversions',
  'CTR bu değerin altına düştüğünde tetiklenir': 'Fires when CTR drops below this value',
  'Günlük bütçenin bu yüzdesi harcandığında tetiklenir': 'Fires when this share of the daily budget is spent',
  'Organik oturum sayısı bu değerin altına düştüğünde tetiklenir': 'Fires when organic sessions drop below this value',
  'Dönüşüm oranı bu değerin altına düştüğünde tetiklenir': 'Fires when conversion rate drops below this value',
  'Seçtiğin zamanda her seferinde çalışır': 'Runs every time at the schedule you pick',
  'Kampanya duraklat': 'Pause campaign',
  'Bütçe düşür (%)': 'Decrease budget (%)',
  'Bütçe artır (%)': 'Increase budget (%)',
  'Mail gönder': 'Send email',
  'Dönüşüm getirmeyen / düşük ROAS’lı kampanyaları durdurur': 'Pauses campaigns with no conversions / low ROAS',
  'Düşük ROAS’lı kampanyaların bütçesini azaltır': 'Cuts the budget of low-ROAS campaigns',
  'Yüksek ROAS’lı kampanyaların bütçesini artırır': 'Raises the budget of high-ROAS campaigns',
  'Sana tetikleyici ayrıntılarıyla e-posta gönderir': 'Emails you the trigger details',
  'Tetikleyici': 'Trigger',
  'Aksiyon': 'Action',
  'Bildirim': 'Notification',
  'Başlangıç': 'Start',
  'Başladığında': 'Starts when',
  'Tetiklendiğinde': 'When triggered',
  'Ardından': 'Then',
  'Ekle': 'Add',
  'Koşul oluşmazsa hiçbir şey yapılmaz': 'Nothing happens when the condition is not met',
  'Tetikleyici seçmek için tıkla': 'Click to select trigger',
  'Aksiyon seçmek için tıkla': 'Click to select action',
  'Boş': 'Empty',
  'Eksik': 'Incomplete',
  'Hazır': 'Ready',
  'Eşik': 'Threshold',
  'Eşik Değeri': 'Threshold Value',
  'Boş bırakırsan varsayılan kullanılır': 'Leave empty to use the default',
  'Varsayılan': 'Default',
  'Platform': 'Platform',
  'Zamanlama': 'Schedule',
  'Her Gün': 'Every Day',
  'Her Pazartesi': 'Every Monday',
  'Yüzde (%)': 'Percentage (%)',
  'Mesaj': 'Message',
  'E-posta bildirimi': 'Email notification',
  'Bildirim e-postası': 'Notification email',
  'Özel mesajlı': 'Custom message',
  'Mail içeriğine eklenecek özel mesaj (boş bırakılırsa tetikleyici bilgileriyle otomatik oluşturulur)':
    'Custom message for the email body (auto-generated from trigger details if left empty)',
  'Bildirim, müşterinin bildirim e-posta adresine ve mail sıklığı ayarına göre gönderilir.':
    'Notifications go to the client’s notification email, following its mail frequency setting.',
  'Bu tetikleyici GA4 tabanlı — kampanya kavramı yok, sadece mail gönderilebilir.':
    'This trigger is GA4-based — there are no campaigns, only email can be sent.',
  'Aksiyonlar onayına düşer; onaylamadan kampanyana dokunulmaz.':
    'Actions land in your approvals; nothing touches your campaigns until you approve.',
  'Bu Adımı Sil': 'Delete This Step',
  'Ayarları': 'Settings',
  'Değiştir': 'Change',
  'Adım Ekle': 'Add Step',
  'Akışa eklenecek adımı seç': 'Pick the step to add to the flow',
  'Tetikleyici çalıştığında kampanyaya uygulanacak işlem': 'What to do to campaigns when the trigger fires',
  'Tetikleyici çalıştığında e-posta ile haber al': 'Get an email when the trigger fires',
  'Kaydedilen Akışlar': 'Saved Flows',
  'Yükleniyor...': 'Loading...',
  'Henüz kaydedilmiş akış yok': 'No saved flows yet',
  'Yükle': 'Load',
  'Aktif': 'Active',
  'Pasif': 'Inactive',
  'Yeni Otomasyon Akışı': 'New Automation Flow',
  'Geri al': 'Undo',
  'İleri al': 'Redo',
  'Akışlarım': 'My Flows',
  'Yeni akış': 'New flow',
  'Akışı Kaydet': 'Save Flow',
  'Kaydet': 'Save',
  'Kaydediliyor...': 'Saving...',
  'Akış kaydedildi ve aktif edildi': 'Flow saved and activated',
  'Akış kaydedilemedi': 'Flow could not be saved',
  'Önce tetikleyiciyi tamamla': 'Complete the trigger first',
  'Bir aksiyon veya bildirim adımı ekle': 'Add an action or notification step',
  'Aksiyon adımını tamamla': 'Complete the action step',
  'Kampanyalarını Otomatikleştir': 'Automate Your Campaigns',
  'Otomasyon akışını nasıl oluşturmak istediğini seç.': 'Choose how you want to create your automation flow.',
  'Sıfırdan Oluştur': 'Build from Scratch',
  'Tetikleyici ve aksiyonları kendin tanımlayarak özel bir akış kur. Tam kontrol, tamamen esnek.':
    'Create a custom flow by defining your own triggers and actions. Full control, fully flexible.',
  'İleri seviye kullanıcılar için önerilir.': 'Recommended for advanced users.',
  'Şablonla Başla': 'Start with Template',
  'Hazır optimizasyon şablonlarıyla hızlıca başla, sonra kurulumu dilediğin gibi özelleştir.':
    'Use pre-built optimization templates to get started quickly and customize your setup.',
  'Hızlı kurulum, düzenlemeye hazır.': 'Quick setup, ready to customize.',
  'AI ile Oluştur': 'Build with AI',
  'Ne istediğini anlat, AI doğru tetikleyici ve aksiyonları birleştirerek akışı senin için kursun.':
    'Describe what you want and AI builds the flow by combining the right triggers and actions.',
  'Akıllı taslak, AI rehberliğinde.': 'Smart draft, AI-guided.',
  'Kaydedilmiş bir akışı aç': 'Open a saved flow',
  'Şablon Seç': 'Choose a Template',
  'Şablonu seç, eksik kalan ayarları akışta tamamla.': 'Pick a template, then finish any missing settings in the flow.',
  'Düşük ROAS’ta bütçeyi kıs': 'Cut budget on low ROAS',
  'ROAS belirlediğin değerin altına düşünce düşük performanslı kampanyaların bütçesini %20 azaltır.':
    'Cuts the budget of underperforming campaigns by 20% when ROAS drops below the value you set.',
  'Kazanan kampanyaları büyüt': 'Scale winning campaigns',
  'ROAS belirlediğin değerin üstüne çıkınca bu kampanyaların bütçesini %15 artırır.':
    'Raises the budget of these campaigns by 15% when ROAS rises above the value you set.',
  'Dönüşümsüz harcamayı durdur': 'Stop spend with no conversions',
  'Harcama yapıp dönüşüm getirmeyen kampanyaları duraklatır ve haber verir.':
    'Pauses campaigns that spend without converting and lets you know.',
  'Düşük CTR uyarısı': 'Low CTR alert',
  'CTR %1’in altına düşünce e-posta ile uyarır.': 'Emails you when CTR drops below 1%.',
  'Bütçe erken bitiyor uyarısı': 'Budget running out early',
  'Günlük bütçenin %80’i harcanınca e-posta ile uyarır.': 'Emails you when 80% of the daily budget is spent.',
  'Organik trafik alarmı': 'Organic traffic alarm',
  'GA4’te organik oturumlar 100’ün altına düşünce uyarır.': 'Alerts you when GA4 organic sessions drop below 100.',
  'Haftalık kontrol e-postası': 'Weekly check-in email',
  'Her pazartesi hesabın için bir kontrol e-postası gönderir.': 'Sends a check-in email for your account every Monday.',
  'Kampanya performansını iyileştirmek için otomasyon akışları oluşturabilirsin.':
    'You can create automation flows to improve your campaign performance.',
  'Örneğin ROAS düştüğünde bütçeyi otomatik kısabilir, dönüşüm getirmeyen kampanyaları durdurabilir veya bir sorun olduğunda e-posta ile haber alabilirsin.':
    'For example, cut budgets automatically when ROAS drops, pause campaigns that don’t convert, or get an email when something goes wrong.',
  'Ne istediğini anlat, akışı senin için kurayım.': 'Tell me what you want and I’ll build the flow.',
  'ROAS 2’nin altına düşerse Meta bütçesini %20 azalt': 'If ROAS drops below 2, cut the Meta budget by 20%',
  'Dönüşüm yoksa Google kampanyasını duraklat': 'Pause the Google campaign if there are no conversions',
  'Organik trafik düşerse bana e-posta gönder': 'Email me if organic traffic drops',
  'Akışı oluşturdum. Kartlara tıklayarak düzenleyebilirsin.': 'I built the flow. Click the cards to edit them.',
  'Akış oluşturuldu!': 'Flow created!',
  'Hata oluştu, tekrar deneyin.': 'An error occurred, please try again.',
  'Oluşturuluyor...': 'Generating...',
  'Akışını anlat veya bir şey sor.': 'Describe your flow or ask a question.',
  'AI Asistan': 'AI Assistant',
  'Asistana dön': 'Back to assistant',
  'Kapat': 'Close',
  'Uzaklaştır': 'Zoom out',
  'Yakınlaştır': 'Zoom in',
  'Ekrana sığdır': 'Fit to screen',
  'Akış adı': 'Flow name',
});

const GOLD = T.accent;
const GREEN = T.green;
const RED = T.red;
const BLUE = T.blue;
const PURPLE = T.purple;
const AMBER = T.amber;

// Local line icons the shared set doesn't have yet.
const IcoBack = (p:any) => <Ico {...p} d="M19 12H5M11 6l-6 6 6 6" sw={2.2}/>;
const IcoX    = (p:any) => <Ico {...p} d="M18 6L6 18M6 6l12 12" sw={2.2}/>;
const IcoMinus= (p:any) => <Ico {...p} d="M5 12h14" sw={2.2}/>;
const IcoFit  = (p:any) => <Ico {...p} d="M4 9V4h5|M20 9V4h-5|M4 15v5h5|M20 15v5h-5" sw={2}/>;
const IcoFlag = (p:any) => <Ico {...p} d="M5 21V4|M5 4h11l-2 4 2 4H5" sw={2}/>;

// value'lar ve platform alanı backend scheduler.py'deki TRIGGER_EVALUATORS /
// FLOW_TRIGGER_DEFAULT_PLATFORM ile birebir aynı. `def` = backend'in eşik boşken kullandığı değer.
const TRIGGER_OPTIONS = [
  { value:'roas_below',   label:'ROAS eşik değerin altında',  platform:'meta,google', def:'', req:true, hint:'ROAS, girdiğin değerin altına düştüğünde tetiklenir.' },
  { value:'roas_above',   label:'ROAS eşik değerin üstünde',  platform:'meta,google', def:'', req:true, hint:'ROAS, girdiğin değerin üstüne çıktığında tetiklenir.' },
  { value:'zero_conv',    label:'Sıfır dönüşüm',         platform:'meta,google', def:'',    hint:"Harcama 200'ü aşıp hiç dönüşüm gelmediğinde tetiklenir." },
  { value:'ctr_low',      label:'CTR düşük',             platform:'meta,google', def:'1',   hint:'CTR bu değerin altına düştüğünde tetiklenir.' },
  { value:'spend_high',   label:'Bütçe erken tüketildi', platform:'meta,google', def:'80',  hint:'Günlük bütçenin bu yüzdesi harcandığında tetiklenir.' },
  { value:'organic_drop', label:'Organik trafik düştü',  platform:'ga4',         def:'100', hint:'Organik oturum sayısı bu değerin altına düştüğünde tetiklenir.' },
  { value:'conv_drop',    label:'Dönüşüm oranı düştü',   platform:'ga4',         def:'1',   hint:'Dönüşüm oranı bu değerin altına düştüğünde tetiklenir.' },
  { value:'schedule',     label:'Zamanlanmış',           platform:'all',         def:'',    hint:'Seçtiğin zamanda her seferinde çalışır.' },
];

const PLATFORM_LABELS: Record<string,string> = { meta:'Meta Ads', google:'Google Ads', ga4:'GA4' };

function platformsFor(triggerValue:string): string[] {
  const opt = TRIGGER_OPTIONS.find(o=>o.value===triggerValue);
  if (!opt) return [];
  return opt.platform === 'all' ? ['meta','google','ga4'] : opt.platform.split(',');
}

// "Reklamı duraklat" (pause_campaign'in aynısıydı) ve "Hedef ROAS güncelle" (Google-only) kaldırıldı.
// pause/bütçe aksiyonları trigger'daki platform'a göre Meta veya Google Ads'e uygulanıyor
// (scheduler.py run_flow_check + automation_router.py). GA4 tetikleyicilerinde kampanya kavramı
// yok — orada yalnızca "Mail gönder" anlamlı.
const ACTION_OPTIONS = [
  { value:'pause_campaign',  label:'Kampanya duraklat', color: RED,   icon:'pause', desc:'Dönüşüm getirmeyen / düşük ROAS’lı kampanyaları durdurur' },
  { value:'decrease_budget', label:'Bütçe düşür (%)',   color: AMBER, icon:'down',  desc:'Düşük ROAS’lı kampanyaların bütçesini azaltır' },
  { value:'increase_budget', label:'Bütçe artır (%)',   color: GREEN, icon:'up',    desc:'Yüksek ROAS’lı kampanyaların bütçesini artırır' },
  { value:'send_email',      label:'Mail gönder',       color: BLUE,  icon:'mail',  desc:'Sana tetikleyici ayrıntılarıyla e-posta gönderir' },
];

function actionOptionsFor(platform:string) {
  return platform==='ga4' ? ACTION_OPTIONS.filter(a=>a.value==='send_email') : ACTION_OPTIONS;
}

function ActionIcon({ kind, size=16 }:{ kind:string; size?:number }) {
  if (kind==='pause') return <Icon.pause size={size} sw={2.2}/>;
  if (kind==='mail')  return <Icon.mail size={size} sw={2}/>;
  if (kind==='down')  return <Ico size={size} sw={2.2} d="M12 5v14M6 13l6 6 6-6"/>;
  if (kind==='up')    return <Ico size={size} sw={2.2} d="M12 19V5M6 11l6-6 6 6"/>;
  return <Icon.play size={size} sw={2}/>;
}

// ── Step model ────────────────────────────────────────────────────────────────
// Backend (scheduler.py) her akışta yalnızca ilk trigger, ilk action ve ilk notify node'unu
// okuyor, kenarları hiç kullanmıyor. Bu yüzden akış doğrusal: Tetikleyici → Aksiyon → Bildirim.
// Kayıt formatı eskisiyle aynı ({nodes, edges}), eski serbest çizim akışlar da açılabiliyor.

type StepType = 'trigger'|'action'|'notify';
type Step = { id:string; type:StepType; data:any };
type Status = 'empty'|'partial'|'ready';

const STEP_ORDER: StepType[] = ['trigger','action','notify'];
const STEP_META: Record<StepType,{ label:string; color:string }> = {
  trigger: { label:'Tetikleyici', color:GOLD },
  action:  { label:'Aksiyon',     color:GREEN },
  notify:  { label:'Bildirim',    color:PURPLE },
};

const newTrigger = (): Step => ({ id:'start', type:'trigger', data:{ trigger:'', platform:'', threshold:'' } });
const INIT_STEPS: Step[] = [newTrigger()];

function normalizeSteps(nodes:any[]): Step[] {
  const out: Step[] = [];
  for (const type of STEP_ORDER) {
    const n = (nodes||[]).find((x:any)=>x?.type===type);
    if (n) out.push({ id:String(n.id), type, data:{ ...(n.data||{}) } });
  }
  if (!out.length || out[0].type!=='trigger') out.unshift(newTrigger());
  return out;
}

function stepsToFlow(steps:Step[]) {
  const nodes = steps.map((s,i)=>({ id:s.id, type:s.type, position:{ x:0, y:120+i*150 }, data:s.data }));
  const edges = steps.slice(1).map((s,i)=>({ id:`e_${steps[i].id}_${s.id}`, source:steps[i].id, target:s.id }));
  return { nodes, edges };
}

function stepStatus(step:Step, triggerPlatform:string): Status {
  const d = step.data||{};
  if (step.type==='trigger') {
    if (!d.trigger) return 'empty';
    if (platformsFor(d.trigger).length>1 && !d.platform) return 'partial';
    if (d.trigger==='schedule' && !d.schedule) return 'partial';
    if ((d.trigger==='roas_below'||d.trigger==='roas_above') && !(parseFloat(String(d.threshold||'').replace(',','.'))>0)) return 'partial';
    return 'ready';
  }
  if (step.type==='action') {
    if (!d.action) return 'empty';
    if (!actionOptionsFor(triggerPlatform).some(a=>a.value===d.action)) return 'partial';
    if ((d.action==='decrease_budget'||d.action==='increase_budget') && !d.value) return 'partial';
    return 'ready';
  }
  return 'ready';
}

function stepSummary(step:Step, t:(s:string)=>string): string[] {
  const d = step.data||{};
  if (step.type==='trigger') {
    const opt = TRIGGER_OPTIONS.find(o=>o.value===d.trigger);
    if (!opt) return [];
    const bits = [t(opt.label)];
    const sub: string[] = [];
    if (d.platform) sub.push(PLATFORM_LABELS[d.platform]||d.platform);
    if (d.trigger==='schedule' && d.schedule) sub.push(t(d.schedule==='weekly_monday'?'Her Pazartesi':'Her Gün'));
    if (opt.req) sub.push(d.threshold ? `${t('Eşik')}: ${d.threshold}` : t('Eşik girilmedi'));
    else if (opt.def) sub.push(`${t('Eşik')}: ${d.threshold||opt.def}${d.threshold?'':` (${t('Varsayılan').toLowerCase()})`}`);
    return sub.length ? [...bits, sub.join(' · ')] : bits;
  }
  if (step.type==='action') {
    const opt = ACTION_OPTIONS.find(a=>a.value===d.action);
    if (!opt) return [];
    const bits = [t(opt.label).replace(' (%)','')];
    if (d.value && (d.action==='decrease_budget'||d.action==='increase_budget')) bits.push(`%${d.value}`);
    return bits;
  }
  return [t('Bildirim e-postası'), ...(d.message ? [t('Özel mesajlı')] : [])];
}

// ── Templates ─────────────────────────────────────────────────────────────────

const TEMPLATES: { name:string; desc:string; color:string; icon:string; steps:Step[] }[] = [
  { name:'Düşük ROAS’ta bütçeyi kıs', desc:'ROAS belirlediğin değerin altına düşünce düşük performanslı kampanyaların bütçesini %20 azaltır.', color:AMBER, icon:'down',
    steps:[{ id:'start', type:'trigger', data:{ trigger:'roas_below', platform:'', threshold:'' } },
           { id:'a1', type:'action', data:{ action:'decrease_budget', value:'20' } },
           { id:'n1', type:'notify', data:{ channel:'email', message:'' } }] },
  { name:'Kazanan kampanyaları büyüt', desc:'ROAS belirlediğin değerin üstüne çıkınca bu kampanyaların bütçesini %15 artırır.', color:GREEN, icon:'up',
    steps:[{ id:'start', type:'trigger', data:{ trigger:'roas_above', platform:'', threshold:'' } },
           { id:'a1', type:'action', data:{ action:'increase_budget', value:'15' } }] },
  { name:'Dönüşümsüz harcamayı durdur', desc:'Harcama yapıp dönüşüm getirmeyen kampanyaları duraklatır ve haber verir.', color:RED, icon:'pause',
    steps:[{ id:'start', type:'trigger', data:{ trigger:'zero_conv', platform:'', threshold:'' } },
           { id:'a1', type:'action', data:{ action:'pause_campaign' } },
           { id:'n1', type:'notify', data:{ channel:'email', message:'' } }] },
  { name:'Düşük CTR uyarısı', desc:'CTR %1’in altına düşünce e-posta ile uyarır.', color:BLUE, icon:'mail',
    steps:[{ id:'start', type:'trigger', data:{ trigger:'ctr_low', platform:'', threshold:'1' } },
           { id:'a1', type:'action', data:{ action:'send_email', message:'' } }] },
  { name:'Bütçe erken bitiyor uyarısı', desc:'Günlük bütçenin %80’i harcanınca e-posta ile uyarır.', color:BLUE, icon:'mail',
    steps:[{ id:'start', type:'trigger', data:{ trigger:'spend_high', platform:'', threshold:'80' } },
           { id:'a1', type:'action', data:{ action:'send_email', message:'' } }] },
  { name:'Organik trafik alarmı', desc:'GA4’te organik oturumlar 100’ün altına düşünce uyarır.', color:PURPLE, icon:'mail',
    steps:[{ id:'start', type:'trigger', data:{ trigger:'organic_drop', platform:'ga4', threshold:'100' } },
           { id:'a1', type:'action', data:{ action:'send_email', message:'' } }] },
  { name:'Haftalık kontrol e-postası', desc:'Her pazartesi hesabın için bir kontrol e-postası gönderir.', color:GOLD, icon:'mail',
    steps:[{ id:'start', type:'trigger', data:{ trigger:'schedule', platform:'', schedule:'weekly_monday' } },
           { id:'a1', type:'action', data:{ action:'send_email', message:'' } }] },
];

// ── Canvas nodes ──────────────────────────────────────────────────────────────

const hiddenHandle: React.CSSProperties = { opacity:0, width:1, height:1, minWidth:0, minHeight:0, border:'none', pointerEvents:'none' };

function StartNode() {
  const { t } = useLang();
  return (
    <div style={{ display:'inline-flex', alignItems:'center', gap:7, padding:'7px 14px', borderRadius:radius.sm,
      background:tint(PURPLE,14), border:`1px solid ${tint(PURPLE,40)}`, color:T.purpleText, fontSize:13, fontWeight:800 }}>
      <IcoFlag size={13}/> {t('Başlangıç')}
      <Handle type="source" position={Position.Bottom} style={hiddenHandle} isConnectable={false}/>
    </div>
  );
}

function StatusBadge({ status }:{ status:Status }) {
  const { t } = useLang();
  const cfg = status==='ready'
    ? { c:T.greenText, bg:T.greenTint, b:tint(GREEN,45), label:t('Hazır'), dash:false }
    : status==='partial'
    ? { c:T.amberText, bg:T.amberTint, b:tint(AMBER,55), label:t('Eksik'), dash:true }
    : { c:T.amberText, bg:T.amberTint, b:tint(AMBER,55), label:t('Boş'), dash:true };
  return (
    <span style={{ flexShrink:0, display:'inline-flex', alignItems:'center', gap:4, fontSize:11, fontWeight:800,
      color:cfg.c, background:cfg.bg, border:`1px ${cfg.dash?'dashed':'solid'} ${cfg.b}`, borderRadius:radius.pill, padding:'3px 9px' }}>
      {status==='ready' && <Icon.check size={11} sw={2.6}/>}{cfg.label}
    </span>
  );
}

function StepNode({ data, selected }: NodeProps) {
  const { t } = useLang();
  const step: Step = data.step;
  const meta = STEP_META[step.type];
  const actionOpt = step.type==='action' ? ACTION_OPTIONS.find(a=>a.value===step.data?.action) : undefined;
  const color = actionOpt?.color || meta.color;
  const summary = stepSummary(step, t);
  const icon = step.type==='trigger' ? <Icon.bolt size={18} sw={2}/>
    : step.type==='notify' ? <Icon.bell size={18} sw={2}/>
    : <ActionIcon kind={actionOpt?.icon||''} size={18}/>;
  const status: Status = data.status;
  return (
    <div style={{ width:data.w, background:T.surface, borderRadius:radius.lg, cursor:'pointer',
      border:`1.5px solid ${selected?color:'var(--py-divider)'}`,
      boxShadow:selected?`0 0 0 4px ${tint(color,85)}, ${shadow.card||'none'}`:'0 1px 2px rgba(20,22,43,.05), 0 8px 24px rgba(20,22,43,.06)',
      padding:'12px 14px', display:'flex', alignItems:'center', gap:12, transition:'border-color .12s, box-shadow .12s' }}>
      <Handle type="target" position={Position.Top} style={hiddenHandle} isConnectable={false}/>
      <div style={{ width:42, height:42, borderRadius:11, flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center',
        background:tint(color,12), color, border:`1px solid ${tint(color,70)}` }}>{icon}</div>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontSize:13.5, fontWeight:800, color:T.text, marginBottom:3 }}>{t(meta.label)}</div>
        {summary.length ? <>
          <div style={{ fontSize:12.5, fontWeight:700, color:T.textMid, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{summary[0]}</div>
          {summary[1] && <div style={{ fontSize:11, fontWeight:600, color:T.textDim, marginTop:2, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{summary[1]}</div>}
        </> : (
          <span style={{ display:'inline-block', fontSize:11.5, fontWeight:700, color:T.greenText, background:T.greenTint,
            border:`1px dashed ${tint(GREEN,45)}`, borderRadius:6, padding:'2px 8px', maxWidth:'100%',
            overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
            {t(step.type==='trigger'?'Tetikleyici seçmek için tıkla':'Aksiyon seçmek için tıkla')}
          </span>
        )}
      </div>
      <StatusBadge status={status}/>
      <Handle type="source" position={Position.Bottom} style={hiddenHandle} isConnectable={false}/>
    </div>
  );
}

function AddNode({ data }: NodeProps) {
  const { t } = useLang();
  return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:14 }}>
      <Handle type="target" position={Position.Top} style={hiddenHandle} isConnectable={false}/>
      {data.canAdd && (
        <button className="nodrag nopan" onClick={(e)=>{ e.stopPropagation(); data.onAdd(); }}
          style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'8px 16px', borderRadius:radius.pill,
            background:T.surface, border:'1px solid var(--py-border-mid)', color:T.text, fontFamily:'inherit',
            fontSize:13, fontWeight:700, cursor:'pointer', boxShadow:'0 1px 2px rgba(20,22,43,.06)' }}>
          <Icon.plus size={14} sw={2.2}/> {t('Ekle')}
        </button>
      )}
      <div style={{ fontSize:12, fontWeight:600, color:T.textMid, background:T.surface, border:'1px solid var(--py-divider)',
        borderRadius:radius.pill, padding:'7px 16px', whiteSpace:'nowrap' }}>
        {t('Koşul oluşmazsa hiçbir şey yapılmaz')}
      </div>
    </div>
  );
}

const nodeTypes = { start:StartNode, step:StepNode, add:AddNode };

// ── Zoom controls (Wask-style: − 92% + ⤢) ─────────────────────────────────────

function ZoomBar() {
  const { t } = useLang();
  const rf = useReactFlow();
  const zoom = useStore(s=>s.transform[2]);
  const btn: React.CSSProperties = { width:36, height:36, display:'flex', alignItems:'center', justifyContent:'center',
    background:'transparent', border:'none', color:T.textMid, cursor:'pointer' };
  return (
    <div style={{ position:'absolute', left:12, bottom:12, zIndex:5, display:'flex', gap:8 }}>
      <div style={{ display:'flex', alignItems:'center', background:T.surface, border:'1px solid var(--py-divider)',
        borderRadius:radius.md, boxShadow:'0 1px 2px rgba(20,22,43,.06)' }}>
        <button style={btn} title={t('Uzaklaştır')} onClick={()=>rf.zoomOut({duration:150})}><IcoMinus size={15}/></button>
        <div style={{ minWidth:50, textAlign:'center', fontSize:12.5, fontWeight:700, color:T.text,
          borderLeft:'1px solid var(--py-divider)', borderRight:'1px solid var(--py-divider)', lineHeight:'36px' }}>
          {Math.round(zoom*100)}%
        </div>
        <button style={btn} title={t('Yakınlaştır')} onClick={()=>rf.zoomIn({duration:150})}><Icon.plus size={15} sw={2.2}/></button>
      </div>
      <button title={t('Ekrana sığdır')} onClick={()=>rf.fitView({padding:0.25, duration:200, maxZoom:1})}
        style={{ ...btn, background:T.surface, border:'1px solid var(--py-divider)', borderRadius:radius.md,
          boxShadow:'0 1px 2px rgba(20,22,43,.06)' }}><IcoFit size={15}/></button>
    </div>
  );
}

// ── Form bits (top-level so inputs keep focus between renders) ────────────────

const fieldStyle: React.CSSProperties = { width:'100%', padding:'10px 12px', borderRadius:radius.sm,
  border:'1px solid var(--py-border-mid)', fontSize:14, outline:'none', background:'var(--py-subtle-bg)',
  boxSizing:'border-box', color:T.text, fontFamily:'inherit' };

function FieldLabel({ children }:{ children:React.ReactNode }) {
  return <div style={{ fontSize:11.5, color:T.textMid, fontWeight:800, marginBottom:8, letterSpacing:'0.02em' }}>{children}</div>;
}

function Segmented({ value, options, onChange }:{ value:string; options:{value:string;label:string}[]; onChange:(v:string)=>void }) {
  return (
    <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
      {options.map(o=>{
        const on = o.value===value;
        return (
          <button key={o.value} onClick={()=>onChange(o.value)} style={{ flex:'1 1 auto', padding:'9px 12px',
            borderRadius:radius.sm, fontFamily:'inherit', fontSize:13, fontWeight:700, cursor:'pointer',
            background:on?tint(GOLD,16):T.surface, color:on?T.text:T.textMid,
            border:`1.5px solid ${on?GOLD:'var(--py-border-mid)'}` }}>{o.label}</button>
        );
      })}
    </div>
  );
}

function OptionRow({ active, color, icon, title, desc, tag, onClick }:{
  active:boolean; color:string; icon:React.ReactNode; title:string; desc?:string; tag?:string; onClick:()=>void;
}) {
  return (
    <button onClick={onClick} style={{ width:'100%', textAlign:'left', display:'flex', alignItems:'center', gap:11,
      padding:'10px 12px', marginBottom:6, borderRadius:radius.md, cursor:'pointer', fontFamily:'inherit',
      background:active?tint(color,10):T.surface, border:`1.5px solid ${active?color:'var(--py-divider)'}` }}>
      <IconChip color={color} size={32}>{icon}</IconChip>
      <span style={{ flex:1, minWidth:0 }}>
        <span style={{ display:'block', fontSize:13, fontWeight:800, color:T.text }}>{title}</span>
        {desc && <span style={{ display:'block', fontSize:11.5, color:T.textDim, marginTop:2, lineHeight:1.4 }}>{desc}</span>}
      </span>
      {tag && <span style={{ flexShrink:0, fontSize:10, fontWeight:800, color:T.textDim, background:'var(--py-subtle-bg)',
        borderRadius:radius.pill, padding:'3px 8px' }}>{tag}</span>}
      {active && <span style={{ color, flexShrink:0 }}><Icon.check size={15} sw={2.6}/></span>}
    </button>
  );
}

function PanelHeader({ color, icon, title, onBack, onClose }:{
  color:string; icon:React.ReactNode; title:string; onBack?:()=>void; onClose?:()=>void;
}) {
  const { t } = useLang();
  const iconBtn: React.CSSProperties = { background:'none', border:'none', cursor:'pointer', color:T.textMid, padding:6,
    display:'flex', borderRadius:8 };
  return (
    <div style={{ padding:'12px 14px', borderBottom:'1px solid var(--py-divider)', display:'flex', alignItems:'center', gap:8, flexShrink:0 }}>
      {onBack && <button style={iconBtn} onClick={onBack} title={t('Asistana dön')}><IcoBack size={16}/></button>}
      <IconChip color={color} size={28}>{icon}</IconChip>
      <span style={{ flex:1, fontSize:14, fontWeight:800, color:T.text }}>{title}</span>
      {onClose && <button style={iconBtn} onClick={onClose} title={t('Kapat')}><IcoX size={16}/></button>}
    </div>
  );
}

// ── Step config panel ─────────────────────────────────────────────────────────

function StepConfigPanel({ step, triggerPlatform, onUpdate, onDelete, onBack, onClose }:{
  step:Step; triggerPlatform:string; onUpdate:(id:string,data:any)=>void; onDelete:(id:string)=>void;
  onBack?:()=>void; onClose?:()=>void;
}) {
  const { t } = useLang();
  const d = step.data||{};
  const upd = (patch:any) => onUpdate(step.id, { ...d, ...patch });
  const [pickTrigger, setPickTrigger] = useState(!d.trigger);
  const meta = STEP_META[step.type];
  const trigOpt = TRIGGER_OPTIONS.find(o=>o.value===d.trigger);
  const headIcon = step.type==='trigger' ? <Icon.bolt size={14} sw={2}/> : step.type==='notify' ? <Icon.bell size={14} sw={2}/> : <Icon.play size={13} sw={2}/>;

  return (
    <div style={{ height:'100%', display:'flex', flexDirection:'column', minHeight:0 }}>
      <PanelHeader color={meta.color} icon={headIcon} title={`${t(meta.label)} ${t('Ayarları')}`} onBack={onBack} onClose={onClose}/>
      <div style={{ flex:1, overflowY:'auto', padding:16, minHeight:0 }}>
        {step.type==='trigger' && <>
          <div style={{ display:'flex', alignItems:'baseline', justifyContent:'space-between' }}>
            <FieldLabel>{t('Tetikleyici')}</FieldLabel>
            {!pickTrigger && <button onClick={()=>setPickTrigger(true)} style={{ background:'none', border:'none', padding:0,
              cursor:'pointer', fontFamily:'inherit', fontSize:12, fontWeight:800, color:T.text, textDecoration:'underline' }}>{t('Değiştir')}</button>}
          </div>
          {TRIGGER_OPTIONS.filter(o=>pickTrigger || o.value===d.trigger).map(o=>(
            <OptionRow key={o.value} active={d.trigger===o.value} color={GOLD}
              icon={o.value==='schedule'?<Icon.calendar size={15} sw={2}/>:<Icon.bolt size={15} sw={2}/>}
              title={t(o.label)} desc={t(o.hint)}
              tag={o.platform==='ga4'?'GA4':o.platform==='all'?undefined:'Meta · Google'}
              onClick={()=>{
                const plats = platformsFor(o.value);
                upd({ trigger:o.value, platform: plats.length===1 ? plats[0] : (plats.includes(d.platform)?d.platform:'') });
                setPickTrigger(false);
              }}/>
          ))}
          {platformsFor(d.trigger).length>1 && (
            <div style={{ marginTop:16 }}>
              <FieldLabel>{t('Platform')}</FieldLabel>
              <Segmented value={d.platform||''} onChange={v=>upd({ platform:v })}
                options={platformsFor(d.trigger).map(p=>({ value:p, label:PLATFORM_LABELS[p] }))}/>
            </div>
          )}
          {(trigOpt?.def || trigOpt?.req) && (
            <div style={{ marginTop:16 }}>
              <FieldLabel>{t('Eşik Değeri')}{d.trigger==='spend_high'?' (%)':''}</FieldLabel>
              <input value={d.threshold||''} inputMode="decimal" onChange={e=>upd({ threshold:e.target.value })}
                placeholder={trigOpt.req ? `${t('Örn.')} 2.5` : `${t('Varsayılan')}: ${trigOpt.def}`} style={fieldStyle}/>
              <div style={{ fontSize:11.5, color:T.textFaint, marginTop:6 }}>{trigOpt.req ? t('Zorunlu: ROAS için varsayılan değer yoktur.') : t('Boş bırakırsan varsayılan kullanılır')}</div>
            </div>
          )}
          {d.trigger==='schedule' && (
            <div style={{ marginTop:16 }}>
              <FieldLabel>{t('Zamanlama')}</FieldLabel>
              <Segmented value={d.schedule||''} onChange={v=>upd({ schedule:v })}
                options={[{ value:'daily', label:t('Her Gün') }, { value:'weekly_monday', label:t('Her Pazartesi') }]}/>
            </div>
          )}
        </>}

        {step.type==='action' && <>
          <FieldLabel>{t('Aksiyon')}</FieldLabel>
          {actionOptionsFor(triggerPlatform).map(a=>(
            <OptionRow key={a.value} active={d.action===a.value} color={a.color} icon={<ActionIcon kind={a.icon} size={15}/>}
              title={t(a.label).replace(' (%)','')} desc={t(a.desc)} onClick={()=>upd({ action:a.value })}/>
          ))}
          {triggerPlatform==='ga4' && (
            <div style={{ fontSize:11.5, color:T.textFaint, marginTop:4 }}>
              {t('Bu tetikleyici GA4 tabanlı — kampanya kavramı yok, sadece mail gönderilebilir.')}
            </div>
          )}
          {(d.action==='decrease_budget'||d.action==='increase_budget') && (
            <div style={{ marginTop:16 }}>
              <FieldLabel>{t('Yüzde (%)')}</FieldLabel>
              <input value={d.value||''} inputMode="numeric" onChange={e=>upd({ value:e.target.value.replace(/[^\d.]/g,'') })}
                placeholder="20" style={fieldStyle}/>
            </div>
          )}
          {d.action==='send_email' && (
            <div style={{ marginTop:16 }}>
              <FieldLabel>{t('Mesaj')}</FieldLabel>
              <textarea value={d.message||''} onChange={e=>upd({ message:e.target.value })} rows={4}
                placeholder={t('Mail içeriğine eklenecek özel mesaj (boş bırakılırsa tetikleyici bilgileriyle otomatik oluşturulur)')}
                style={{ ...fieldStyle, resize:'vertical' }}/>
            </div>
          )}
          {d.action && d.action!=='send_email' && (
            <div style={{ marginTop:14, fontSize:12, color:T.textMid, background:'var(--py-subtle-bg)', borderRadius:radius.sm,
              padding:'10px 12px', lineHeight:1.5 }}>
              {t('Aksiyonlar onayına düşer; onaylamadan kampanyana dokunulmaz.')}
            </div>
          )}
        </>}

        {step.type==='notify' && <>
          <FieldLabel>{t('Mesaj')}</FieldLabel>
          <textarea value={d.message||''} onChange={e=>upd({ message:e.target.value, channel:'email' })} rows={4}
            placeholder={t('Mail içeriğine eklenecek özel mesaj (boş bırakılırsa tetikleyici bilgileriyle otomatik oluşturulur)')}
            style={{ ...fieldStyle, resize:'vertical' }}/>
          <div style={{ fontSize:11.5, color:T.textFaint, marginTop:8, lineHeight:1.5 }}>
            {t('Bildirim, müşterinin bildirim e-posta adresine ve mail sıklığı ayarına göre gönderilir.')}
          </div>
        </>}
      </div>
      {step.type!=='trigger' && (
        <div style={{ padding:'12px 16px', borderTop:'1px solid var(--py-divider)', flexShrink:0 }}>
          <Button variant="soft" color={RED} onClick={()=>onDelete(step.id)} style={{ width:'100%' }}>
            <Icon.trash size={13} sw={2}/> {t('Bu Adımı Sil')}
          </Button>
        </div>
      )}
    </div>
  );
}

// ── Add-step picker ───────────────────────────────────────────────────────────

function AddStepPanel({ available, onPick, onBack, onClose }:{
  available:StepType[]; onPick:(t:StepType)=>void; onBack?:()=>void; onClose?:()=>void;
}) {
  const { t } = useLang();
  const info: Record<string,{ icon:React.ReactNode; desc:string }> = {
    action: { icon:<Icon.play size={15} sw={2}/>, desc:'Tetikleyici çalıştığında kampanyaya uygulanacak işlem' },
    notify: { icon:<Icon.bell size={15} sw={2}/>, desc:'Tetikleyici çalıştığında e-posta ile haber al' },
  };
  return (
    <div style={{ height:'100%', display:'flex', flexDirection:'column', minHeight:0 }}>
      <PanelHeader color={GOLD} icon={<Icon.plus size={14} sw={2.2}/>} title={t('Adım Ekle')} onBack={onBack} onClose={onClose}/>
      <div style={{ flex:1, overflowY:'auto', padding:16 }}>
        <div style={{ fontSize:12.5, color:T.textDim, marginBottom:12 }}>{t('Akışa eklenecek adımı seç')}</div>
        {available.map(type=>(
          <OptionRow key={type} active={false} color={STEP_META[type].color} icon={info[type].icon}
            title={t(STEP_META[type].label)} desc={t(info[type].desc)} onClick={()=>onPick(type)}/>
        ))}
      </div>
    </div>
  );
}

// ── AI Assistant (Wask-style side chat) ───────────────────────────────────────

const AI_SYSTEM = `Sen Poyais One otomasyon asistanısın. Kullanıcının isteğinden bir reklam otomasyon akışı kur.
SADECE şu JSON'u döndür, başka hiçbir şey yazma:
{"message":"kullanıcının dilinde 1-2 cümle açıklama","flow":{"name":"kısa akış adı","trigger":{"trigger":"...","platform":"...","threshold":"...","schedule":"..."},"action":{"action":"...","value":"...","message":"..."},"notify":{"message":"..."}}}
Kurallar:
- trigger.trigger şunlardan biri: roas_below (eşik varsayılan 2), roas_above (6), zero_conv (eşik yok), ctr_low (1), spend_high (günlük bütçe %, 80), organic_drop (GA4 organik oturum, 100), conv_drop (GA4 dönüşüm oranı, 1), schedule (schedule: daily | weekly_monday).
- trigger.platform: meta | google | ga4. organic_drop ve conv_drop için her zaman ga4. Kullanıcı platform belirtmezse boş bırak.
- action.action: pause_campaign | decrease_budget | increase_budget | send_email. Bütçe aksiyonlarında value yüzde (örn "20"). GA4 tetikleyicilerinde yalnızca send_email.
- Aksiyon gerekmiyorsa "action": null; bildirim istenmiyorsa "notify": null.
- Kullanıcı akış değil bilgi isterse "flow": null döndür ve message'da cevapla.`;

function AIAssistant({ onFlow, onClose, autoFocus }:{
  onFlow:(steps:Step[], name?:string)=>void; onClose?:()=>void; autoFocus?:boolean;
}) {
  const { t, lang } = useLang();
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [msgs, setMsgs] = useState<{role:'ai'|'user'; text:string}[]>([]);
  const endRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(()=>{ endRef.current?.scrollIntoView({ behavior:'smooth', block:'end' }); },[msgs, loading]);
  useEffect(()=>{ if (autoFocus) inputRef.current?.focus(); },[autoFocus]);

  const send = async (text?:string) => {
    const msg = (text ?? prompt).trim();
    if (!msg || loading) return;
    setPrompt('');
    setMsgs(p=>[...p, { role:'user', text:msg }]);
    setLoading(true);
    try {
      const token = localStorage.getItem('poyais_token')||localStorage.getItem('token')||'';
      const res = await fetch('/api/v1/intelligence/ai/proxy', {
        method:'POST',
        headers:{ 'Content-Type':'application/json', 'Authorization':'Bearer '+token },
        body:JSON.stringify({ system:AI_SYSTEM + `\nmessage alanını ${lang==='en'?'İngilizce':'Türkçe'} yaz.`, user:msg, max_tokens:800 }),
      });
      const data = await res.json();
      const raw: string = data.content||'';
      const m = raw.match(/```json\s*([\s\S]*?)```/) || raw.match(/(\{[\s\S]*\})/);
      const parsed = JSON.parse(m ? m[1].trim() : raw.trim());
      const f = parsed.flow;
      if (f && f.trigger) {
        const steps: Step[] = [{ id:'start', type:'trigger', data:{ trigger:f.trigger.trigger||'', platform:f.trigger.platform||'',
          threshold:f.trigger.threshold?String(f.trigger.threshold):'', ...(f.trigger.schedule?{schedule:f.trigger.schedule}:{}) } }];
        const trig = steps[0].data;
        const plats = platformsFor(trig.trigger);
        if (plats.length===1) trig.platform = plats[0];
        else if (!plats.includes(trig.platform)) trig.platform = '';
        if (f.action && f.action.action) steps.push({ id:`a_${Date.now()}`, type:'action', data:{ action:f.action.action,
          ...(f.action.value?{ value:String(f.action.value) }:{}), ...(f.action.message?{ message:f.action.message }:{}) } });
        if (f.notify) steps.push({ id:`n_${Date.now()}`, type:'notify', data:{ channel:'email', message:f.notify.message||'' } });
        onFlow(steps, f.name);
        setMsgs(p=>[...p, { role:'ai', text:(parsed.message||t('Akış oluşturuldu!')) + '\n\n' + t('Akışı oluşturdum. Kartlara tıklayarak düzenleyebilirsin.') }]);
      } else {
        setMsgs(p=>[...p, { role:'ai', text:parsed.message||t('Hata oluştu, tekrar deneyin.') }]);
      }
    } catch { setMsgs(p=>[...p, { role:'ai', text:t('Hata oluştu, tekrar deneyin.') }]); }
    setLoading(false);
  };

  const suggestions = [
    'ROAS 2’nin altına düşerse Meta bütçesini %20 azalt',
    'Dönüşüm yoksa Google kampanyasını duraklat',
    'Organik trafik düşerse bana e-posta gönder',
  ];

  return (
    <div style={{ height:'100%', display:'flex', flexDirection:'column', minHeight:0 }}>
      {onClose && <PanelHeader color={GOLD} icon={<Icon.sparkle size={14} sw={1.9}/>} title={t('AI Asistan')} onClose={onClose}/>}
      <div style={{ flex:1, overflowY:'auto', padding:'18px 18px 8px', minHeight:0 }}>
        <div style={{ display:'flex', gap:12, marginBottom:16 }}>
          <div style={{ flexShrink:0, marginTop:2 }}><IconChip color={GOLD} size={30}><Icon.sparkle size={15} sw={1.9}/></IconChip></div>
          <div style={{ fontSize:14, lineHeight:1.65, color:T.textMid }}>
            <div style={{ color:T.text, fontWeight:600, marginBottom:8 }}>{t('Kampanya performansını iyileştirmek için otomasyon akışları oluşturabilirsin.')}</div>
            <div style={{ marginBottom:8 }}>{t('Örneğin ROAS düştüğünde bütçeyi otomatik kısabilir, dönüşüm getirmeyen kampanyaları durdurabilir veya bir sorun olduğunda e-posta ile haber alabilirsin.')}</div>
            <div>{t('Ne istediğini anlat, akışı senin için kurayım.')}</div>
          </div>
        </div>
        {msgs.length===0 && (
          <div style={{ display:'flex', flexDirection:'column', gap:8, marginLeft:42 }}>
            {suggestions.map(s=>(
              <button key={s} onClick={()=>send(t(s))} style={{ textAlign:'left', padding:'10px 14px', borderRadius:radius.md,
                background:T.surface, border:'1px solid var(--py-divider)', color:T.text, fontFamily:'inherit', fontSize:13,
                fontWeight:600, cursor:'pointer', display:'flex', alignItems:'center', gap:9 }}>
                <span style={{ color:GOLD, flexShrink:0, display:'flex' }}><Icon.bolt size={14} sw={2}/></span>{t(s)}
              </button>
            ))}
          </div>
        )}
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {msgs.map((m,i)=>(
            <div key={i} style={{ alignSelf:m.role==='user'?'flex-end':'flex-start', maxWidth:'88%', whiteSpace:'pre-wrap',
              marginLeft:m.role==='ai'?42:0,
              background:m.role==='user'?tint(GOLD,12):'var(--py-subtle-bg)',
              border:m.role==='user'?`1px solid ${tint(GOLD,40)}`:'1px solid var(--py-divider)',
              borderRadius:m.role==='user'?'14px 14px 3px 14px':'14px 14px 14px 3px',
              padding:'10px 14px', fontSize:13.5, color:T.text, lineHeight:1.6 }}>{m.text}</div>
          ))}
          {loading && <div style={{ alignSelf:'flex-start', marginLeft:42, background:'var(--py-subtle-bg)', border:'1px solid var(--py-divider)',
            borderRadius:'14px 14px 14px 3px', padding:'10px 14px', display:'flex', alignItems:'center', gap:6, fontSize:13, color:T.textMid }}>
            <Icon.sparkle size={13} sw={1.9} color={GOLD}/>{t('Oluşturuluyor...')}</div>}
        </div>
        <div ref={endRef}/>
      </div>
      <div style={{ padding:12, flexShrink:0 }}>
        <div style={{ border:'1px solid var(--py-border-mid)', borderRadius:radius.xl, background:T.surface,
          boxShadow:'0 2px 12px rgba(20,22,43,.06)', padding:'12px 12px 10px 14px' }}>
          <textarea ref={inputRef} value={prompt} onChange={e=>setPrompt(e.target.value)} rows={2}
            onKeyDown={e=>{ if (e.key==='Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder={t('Akışını anlat veya bir şey sor.')}
            style={{ width:'100%', border:'none', outline:'none', resize:'none', background:'transparent', color:T.text,
              fontFamily:'inherit', fontSize:14, lineHeight:1.5, boxSizing:'border-box' }}/>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'flex-end' }}>
            <button onClick={()=>send()} disabled={loading||!prompt.trim()} style={{ width:34, height:34, borderRadius:'50%',
              border:'none', display:'flex', alignItems:'center', justifyContent:'center', cursor:loading||!prompt.trim()?'default':'pointer',
              background:loading||!prompt.trim()?'var(--py-track-bg)':GOLD, color:loading||!prompt.trim()?T.textFaint:'#fff' }}>
              <Ico size={16} sw={2.3} d="M12 19V5M6 11l6-6 6 6"/>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Start modal (Sıfırdan / Şablon / AI) ──────────────────────────────────────

function StartModal({ isMobile, hasSaved, onScratch, onTemplate, onAI, onOpenSaved, onClose }:{
  isMobile:boolean; hasSaved:boolean; onScratch:()=>void; onTemplate:(tpl:typeof TEMPLATES[number])=>void;
  onAI:()=>void; onOpenSaved:()=>void; onClose:()=>void;
}) {
  const { t } = useLang();
  const [view, setView] = useState<'choose'|'templates'>('choose');
  const cards = [
    { key:'scratch', color:GREEN,  icon:<Icon.plus size={26} sw={2}/>, title:'Sıfırdan Oluştur', onClick:onScratch,
      desc:'Tetikleyici ve aksiyonları kendin tanımlayarak özel bir akış kur. Tam kontrol, tamamen esnek.', foot:'İleri seviye kullanıcılar için önerilir.' },
    { key:'template', color:BLUE,  icon:<Icon.grid size={24} sw={2}/>, title:'Şablonla Başla', onClick:()=>setView('templates'),
      desc:'Hazır optimizasyon şablonlarıyla hızlıca başla, sonra kurulumu dilediğin gibi özelleştir.', foot:'Hızlı kurulum, düzenlemeye hazır.' },
    { key:'ai', color:PURPLE, icon:<Icon.sparkle size={26} sw={1.9}/>, title:'AI ile Oluştur', onClick:onAI,
      desc:'Ne istediğini anlat, AI doğru tetikleyici ve aksiyonları birleştirerek akışı senin için kursun.', foot:'Akıllı taslak, AI rehberliğinde.' },
  ];
  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, zIndex:2000, background:'rgba(15,17,30,.45)',
      backdropFilter:'blur(3px)', display:'flex', alignItems:isMobile?'flex-end':'center', justifyContent:'center' }}>
      <div onClick={e=>e.stopPropagation()} style={{ position:'relative', width:'100%', maxWidth:isMobile?'100%':1040,
        maxHeight:isMobile?'92%':'88vh', overflowY:'auto', background:T.surface, margin:isMobile?0:'0 20px',
        borderRadius:isMobile?'22px 22px 0 0':24, boxShadow:shadow.pop, padding:isMobile?'22px 16px 20px':'38px 34px 34px',
        boxSizing:'border-box' }}>
        <button onClick={onClose} title={t('Kapat')} style={{ position:'absolute', top:14, right:14, background:'none', border:'none',
          cursor:'pointer', color:T.textFaint, padding:6, display:'flex' }}><IcoX size={20}/></button>

        {view==='choose' ? <>
          <div style={{ textAlign:'center', marginBottom:isMobile?18:34, padding:'0 24px' }}>
            <div style={{ fontSize:isMobile?19:25, fontWeight:800, color:T.text, marginBottom:8 }}>{t('Kampanyalarını Otomatikleştir')}</div>
            <div style={{ fontSize:isMobile?13.5:16, color:T.textMid }}>{t('Otomasyon akışını nasıl oluşturmak istediğini seç.')}</div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:isMobile?'1fr':'repeat(3, 1fr)', gap:isMobile?10:22 }}>
            {cards.map(c=>(
              <button key={c.key} onClick={c.onClick} className="fb-start-card" style={{ textAlign:isMobile?'left':'center', padding:0,
                background:'var(--py-subtle-bg)', border:'1px solid var(--py-divider)', borderRadius:18, cursor:'pointer',
                fontFamily:'inherit', overflow:'hidden', display:'flex', flexDirection:'column' }}>
                <div style={{ background:T.surface, borderRadius:18, border:'1px solid var(--py-divider)', margin:-1,
                  padding:isMobile?'14px':'34px 24px 30px', flex:1, display:'flex', flexDirection:isMobile?'row':'column',
                  alignItems:isMobile?'flex-start':'center', gap:isMobile?12:0 }}>
                  <span style={{ color:c.color, marginBottom:isMobile?0:18, display:'flex', flexShrink:0,
                    ...(isMobile?{ width:40, height:40, borderRadius:11, background:tint(c.color,12), alignItems:'center', justifyContent:'center' }:{}) }}>{c.icon}</span>
                  <span style={{ minWidth:0 }}>
                    <span style={{ display:'block', fontSize:isMobile?15:18, fontWeight:800, color:T.text, marginBottom:isMobile?3:12 }}>{t(c.title)}</span>
                    <span style={{ display:'block', fontSize:isMobile?12.5:14.5, color:T.textMid, lineHeight:1.6 }}>{t(c.desc)}</span>
                  </span>
                </div>
                {!isMobile && <div style={{ padding:'14px 12px', fontSize:13.5, color:T.text, fontWeight:600, textAlign:'center' }}>{t(c.foot)}</div>}
              </button>
            ))}
          </div>
          {hasSaved && (
            <div style={{ textAlign:'center', marginTop:isMobile?16:24 }}>
              <button onClick={onOpenSaved} style={{ background:'none', border:'none', cursor:'pointer', fontFamily:'inherit',
                color:T.textMid, fontSize:13.5, fontWeight:700, display:'inline-flex', alignItems:'center', gap:7 }}>
                <Icon.folder size={15} sw={1.9}/> {t('Kaydedilmiş bir akışı aç')}
              </button>
            </div>
          )}
        </> : <>
          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:6, paddingRight:30 }}>
            <button onClick={()=>setView('choose')} style={{ background:'none', border:'none', cursor:'pointer', color:T.textMid, padding:6, display:'flex' }}><IcoBack size={18}/></button>
            <div style={{ fontSize:isMobile?18:22, fontWeight:800, color:T.text }}>{t('Şablon Seç')}</div>
          </div>
          <div style={{ fontSize:13.5, color:T.textMid, marginBottom:18, marginLeft:40 }}>{t('Şablonu seç, eksik kalan ayarları akışta tamamla.')}</div>
          <div style={{ display:'grid', gridTemplateColumns:isMobile?'1fr':'repeat(auto-fill, minmax(280px, 1fr))', gap:12 }}>
            {TEMPLATES.map(tpl=>{
              const trig = TRIGGER_OPTIONS.find(o=>o.value===tpl.steps[0].data.trigger);
              return (
                <button key={tpl.name} onClick={()=>onTemplate(tpl)} className="fb-start-card" style={{ textAlign:'left', padding:16,
                  background:T.surface, border:'1px solid var(--py-divider)', borderRadius:16, cursor:'pointer', fontFamily:'inherit',
                  display:'flex', gap:12, alignItems:'flex-start' }}>
                  <IconChip color={tpl.color} size={38}><ActionIcon kind={tpl.icon} size={17}/></IconChip>
                  <span style={{ minWidth:0 }}>
                    <span style={{ display:'block', fontSize:14.5, fontWeight:800, color:T.text, marginBottom:4 }}>{t(tpl.name)}</span>
                    <span style={{ display:'block', fontSize:12.5, color:T.textMid, lineHeight:1.55, marginBottom:8 }}>{t(tpl.desc)}</span>
                    <span style={{ fontSize:10.5, fontWeight:800, color:T.textDim, background:'var(--py-subtle-bg)', borderRadius:radius.pill, padding:'3px 9px' }}>
                      {trig?.platform==='ga4' ? 'GA4' : trig?.platform==='all' ? 'Meta · Google · GA4' : 'Meta · Google'}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </>}
      </div>
    </div>
  );
}

// ── Saved flows ───────────────────────────────────────────────────────────────

const flowsCacheKey = (cid:number) => `poyais_flows_${cid}`;

function SavedFlowsPanel({ clientId, onClose, onLoad }:{
  clientId:number; onClose:()=>void; onLoad:(nodes:any[], name:string)=>void;
}) {
  const { t, lang } = useLang();
  const [flows, setFlows] = useState<any[]>(()=>{
    try { return JSON.parse(localStorage.getItem(flowsCacheKey(clientId))||'[]'); } catch { return []; }
  });
  const [loading, setLoading] = useState(true);

  useEffect(()=>{
    const token = localStorage.getItem('poyais_token')||localStorage.getItem('token')||'';
    fetch(`/api/v1/automation/flows?client_id=${clientId}`, { headers:{ 'Authorization':'Bearer '+token } })
      .then(r=>r.ok?r.json():Promise.reject())
      .then(data=>{
        if (Array.isArray(data)) {
          const list = data.map((f:any)=>{
            try {
              const parsed = JSON.parse(f.flow_json||'{}');
              return { name:f.name, nodes:parsed.nodes||[], updatedAt:f.updated_at, id:f.id, is_active:f.is_active };
            } catch { return null; }
          }).filter(Boolean);
          setFlows(list);
          try { localStorage.setItem(flowsCacheKey(clientId), JSON.stringify(list)); } catch {}
        }
      })
      .catch(()=>{})
      .finally(()=>setLoading(false));
  },[clientId]);

  const del = (f:any) => {
    const token = localStorage.getItem('poyais_token')||localStorage.getItem('token')||'';
    if (f.id) fetch(`/api/v1/automation/flows/${f.id}`, { method:'DELETE', headers:{ 'Authorization':'Bearer '+token } }).catch(()=>{});
    const u = flows.filter(x=>x.name!==f.name);
    try { localStorage.setItem(flowsCacheKey(clientId), JSON.stringify(u)); } catch {}
    setFlows(u);
  };

  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(15,17,30,.45)', backdropFilter:'blur(3px)', zIndex:2100,
      display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div onClick={e=>e.stopPropagation()} style={{ width:'100%', maxWidth:560, maxHeight:'80vh', display:'flex', flexDirection:'column',
        margin:'0 16px', background:T.surface, borderRadius:radius.xl, boxShadow:shadow.pop, overflow:'hidden' }}>
        <PanelHeader color={GOLD} icon={<Icon.folder size={14} sw={2}/>} title={t('Kaydedilen Akışlar')} onClose={onClose}/>
        <div style={{ flex:1, overflowY:'auto', padding:'12px 16px' }}>
          {loading && flows.length===0 ? (
            <PoyaisSpinner size={36} pad={24}/>
          ) : flows.length===0 ? (
            <div style={{ textAlign:'center', padding:'40px 0', color:T.textFaint, fontSize:13 }}>
              <div style={{ marginBottom:10, display:'flex', justifyContent:'center' }}><Icon.folder size={26} sw={1.6} color={T.textFaint}/></div>
              {t('Henüz kaydedilmiş akış yok')}
            </div>
          ) : flows.map(f=>(
            <div key={f.id||f.name} style={{ border:'1px solid var(--py-divider)', borderRadius:radius.md,
              padding:'12px 14px', marginBottom:8, display:'flex', alignItems:'center', gap:10 }}>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:13.5, fontWeight:700, color:T.text, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{f.name}</div>
                <div style={{ fontSize:11, color:T.textDim, marginTop:3, display:'flex', gap:8, alignItems:'center' }}>
                  {f.is_active!==undefined && <span style={{ fontWeight:800, color:f.is_active?T.greenText:T.textFaint }}>{t(f.is_active?'Aktif':'Pasif')}</span>}
                  {f.updatedAt ? new Date(f.updatedAt).toLocaleDateString(lang==='en'?'en-US':'tr-TR') : ''}
                </div>
              </div>
              <Button variant="soft" color={GOLD} onClick={()=>{ onLoad(f.nodes, f.name); onClose(); }} style={{ flexShrink:0 }}>{t('Yükle')}</Button>
              <Button variant="ghost" onClick={()=>del(f)} style={{ flexShrink:0, padding:'9px 10px', color:T.textMid }}><Icon.trash size={13} sw={2}/></Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────

type PanelMode = 'ai'|'config'|'add';

function FlowBuilderInner({ config }:{ config:any }) {
  const { t } = useLang();
  const { isMobile, isNarrow, isTablet } = useViewport();
  const rf = useReactFlow();
  const clientId: number = Number(config?.activeClientId || config?.client_id
    || parseInt(localStorage.getItem('poyais_active_client')||'0', 10)) || 1;

  const [steps, setSteps] = useState<Step[]>(INIT_STEPS);
  const [selectedId, setSelectedId] = useState<string|null>(null);
  const [panel, setPanel] = useState<PanelMode>('ai');
  const [sheetOpen, setSheetOpen] = useState(false);          // mobile bottom sheet
  const [aiFocus, setAiFocus] = useState(false);
  const [flowName, setFlowName] = useState(t('Yeni Otomasyon Akışı'));
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ ok:boolean; text:string }|null>(null);
  const [showStart, setShowStart] = useState(true);
  const [showFlows, setShowFlows] = useState(false);
  const hasSaved = useMemo(()=>{
    try { return JSON.parse(localStorage.getItem(flowsCacheKey(clientId))||'[]').length>0; } catch { return false; }
  },[clientId, showFlows]);

  const history = useRef<Step[][]>([INIT_STEPS]);
  const histIdx = useRef(0);

  // Parent padding'i kaldır + mobil vh düzeltmesi (App.tsx'teki .flow-builder-active kuralı)
  useEffect(()=>{
    const setVH = () => document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`);
    setVH();
    window.addEventListener('resize', setVH);
    const parent = document.querySelector('.content-padding') as HTMLElement|null;
    parent?.classList.add('flow-builder-active');
    return ()=>{ parent?.classList.remove('flow-builder-active'); window.removeEventListener('resize', setVH); };
  },[]);

  const commit = useCallback((next:Step[])=>{
    history.current = history.current.slice(0, histIdx.current+1);
    history.current.push(next);
    histIdx.current = history.current.length-1;
    setSteps(next);
  },[]);
  const undo = () => { if (histIdx.current>0) { histIdx.current--; setSteps(history.current[histIdx.current]); } };
  const redo = () => { if (histIdx.current<history.current.length-1) { histIdx.current++; setSteps(history.current[histIdx.current]); } };

  const resetTo = (next:Step[], name?:string) => {
    history.current = [next]; histIdx.current = 0;
    setSteps(next); setSelectedId(null);
    if (name) setFlowName(name);
  };

  const showToast = (ok:boolean, text:string) => { setToast({ ok, text }); setTimeout(()=>setToast(null), 3200); };

  const triggerPlatform = steps.find(s=>s.type==='trigger')?.data?.platform || '';
  const available = STEP_ORDER.filter(tp=>tp!=='trigger' && !steps.some(s=>s.type===tp));
  const selectedStep = steps.find(s=>s.id===selectedId) || null;

  const openPanel = (mode:PanelMode) => { setPanel(mode); if (isMobile) setSheetOpen(true); };
  const selectStep = (id:string) => { setSelectedId(id); openPanel('config'); };
  const backToAI = () => { setSelectedId(null); setPanel('ai'); };
  const closeSheet = () => { setSheetOpen(false); setSelectedId(null); setPanel('ai'); };

  const updateStep = (id:string, data:any) => commit(steps.map(s=>s.id===id?{ ...s, data }:s));
  const deleteStep = (id:string) => { commit(steps.filter(s=>s.id!==id)); backToAI(); if (isMobile) setSheetOpen(false); };
  const addStep = (type:StepType) => {
    const st: Step = { id:`node_${Date.now()}`, type, data: type==='notify' ? { channel:'email', message:'' } : {} };
    const next = [...steps, st].sort((a,b)=>STEP_ORDER.indexOf(a.type)-STEP_ORDER.indexOf(b.type));
    commit(next); selectStep(st.id);
  };
  const onAddClick = () => { if (available.length===1) addStep(available[0]); else { setSelectedId(null); openPanel('add'); } };

  // ── Graph (auto layout, centered column) ──
  const cardW = isNarrow ? 272 : 320;
  const { rfNodes, rfEdges } = useMemo(()=>{
    const ns: Node[] = [{ id:'__start', type:'start', position:{ x:0, y:0 }, data:{}, selectable:false }];
    const es: Edge[] = [];
    const edgeBase = { type:'straight', style:{ stroke:'var(--py-border-mid)', strokeWidth:1.5 },
      markerEnd:{ type:MarkerType.ArrowClosed, width:16, height:16, color:'var(--py-text-faint)' },
      labelStyle:{ fontSize:12, fontWeight:600, fill:'var(--py-text-mid)' },
      labelBgStyle:{ fill:'var(--py-subtle-bg)', stroke:'var(--py-divider)' }, labelBgPadding:[8,4] as [number,number], labelBgBorderRadius:6 };
    let prev = '__start';
    let y = 130;
    steps.forEach((s,i)=>{
      ns.push({ id:s.id, type:'step', position:{ x:0, y }, selected:s.id===selectedId,
        data:{ step:s, w:cardW, status:stepStatus(s, triggerPlatform) } });
      es.push({ id:`e_${prev}_${s.id}`, source:prev, target:s.id, ...edgeBase,
        label: t(i===0?'Başladığında':s.type==='action'||i===1?'Tetiklendiğinde':'Ardından') });
      prev = s.id; y += 170;
    });
    ns.push({ id:'__add', type:'add', position:{ x:0, y:y-40 }, selectable:false, data:{ canAdd:available.length>0, onAdd:onAddClick } });
    es.push({ id:`e_${prev}_add`, source:prev, target:'__add', ...edgeBase, style:{ ...edgeBase.style, strokeDasharray:'5 5' } });
    return { rfNodes:ns, rfEdges:es };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[steps, selectedId, cardW, triggerPlatform, t, available.length, isMobile]);

  // Adım sayısı / panel genişliği değişince akışı ekrana yeniden sığdır
  useEffect(()=>{
    const id = setTimeout(()=>rf.fitView({ padding:0.25, duration:250, maxZoom:1 }), 60);
    return ()=>clearTimeout(id);
  },[steps.length, isMobile, isTablet, rf]);

  const validate = (): { ok:boolean; msg?:string; focus?:string } => {
    const trig = steps.find(s=>s.type==='trigger')!;
    if (stepStatus(trig, triggerPlatform)!=='ready') return { ok:false, msg:t('Önce tetikleyiciyi tamamla'), focus:trig.id };
    if (['roas_below','roas_above'].includes(trig.data?.trigger) && !(parseFloat(String(trig.data?.threshold||'').replace(',','.')) > 0)) return { ok:false, msg:t('Önce ROAS eşik değerini gir'), focus:trig.id };
    const act = steps.find(s=>s.type==='action');
    const notif = steps.find(s=>s.type==='notify');
    if (!act && !notif) return { ok:false, msg:t('Bir aksiyon veya bildirim adımı ekle') };
    if (act && stepStatus(act, triggerPlatform)!=='ready') return { ok:false, msg:t('Aksiyon adımını tamamla'), focus:act.id };
    return { ok:true };
  };
  const isValid = validate().ok;

  const saveFlow = async () => {
    const v = validate();
    if (!v.ok) {
      showToast(false, v.msg||'');
      if (v.focus) selectStep(v.focus); else if (available.length) onAddClick();
      return;
    }
    setSaving(true);
    const token = localStorage.getItem('poyais_token')||localStorage.getItem('token')||'';
    const name = flowName.trim() || t('Yeni Otomasyon Akışı');
    try {
      const res = await fetch(`/api/v1/automation/flows?client_id=${clientId}`, {
        method:'POST',
        headers:{ 'Content-Type':'application/json', 'Authorization':'Bearer '+token },
        body:JSON.stringify({ name, flow_json:JSON.stringify(stepsToFlow(steps)), is_active:true }),
      });
      if (!res.ok) {
        let detail = '';
        try { const j = await res.json(); detail = typeof j.detail==='string' ? j.detail : (j.detail?.message||''); } catch {}
        throw new Error(detail);
      }
      try {
        const key = flowsCacheKey(clientId);
        const all = JSON.parse(localStorage.getItem(key)||'[]');
        const entry = { name, nodes:stepsToFlow(steps).nodes, updatedAt:new Date().toISOString(), is_active:true };
        const idx = all.findIndex((f:any)=>f.name===name);
        if (idx>=0) all[idx] = { ...all[idx], ...entry }; else all.unshift(entry);
        localStorage.setItem(key, JSON.stringify(all));
      } catch {}
      showToast(true, t('Akış kaydedildi ve aktif edildi'));
    } catch (e:any) {
      showToast(false, e?.message ? `${t('Akış kaydedilemedi')}: ${e.message}` : t('Akış kaydedilemedi'));
    }
    setSaving(false);
  };

  const startAI = () => { setShowStart(false); resetTo(INIT_STEPS.map(s=>({ ...s, data:{ ...s.data } }))); openPanel('ai'); setAiFocus(true); };

  const panelBody = panel==='config' && selectedStep ? (
    <StepConfigPanel key={selectedStep.id} step={selectedStep} triggerPlatform={triggerPlatform}
      onUpdate={updateStep} onDelete={deleteStep}
      onBack={isMobile?undefined:backToAI} onClose={isMobile?closeSheet:undefined}/>
  ) : panel==='add' ? (
    <AddStepPanel available={available} onPick={addStep} onBack={isMobile?undefined:backToAI} onClose={isMobile?closeSheet:undefined}/>
  ) : (
    <AIAssistant autoFocus={aiFocus} onClose={isMobile?closeSheet:undefined}
      onFlow={(ns, name)=>{ commit(ns); if (name) setFlowName(name); if (isMobile) setSheetOpen(false); }}/>
  );

  const iconBtn: React.CSSProperties = { padding:0, width:38, height:38, flexShrink:0, border:'1px solid var(--py-border-mid)', background:T.surface };

  return (
    <div style={{ display:'flex', flexDirection:'column', height:'calc(var(--vh, 1vh) * 100 - 56px)', background:T.surface,
      overflow:'hidden', fontFamily:FONT, position:'relative' }}>
      <style>{`
        .fb-canvas .react-flow__node{cursor:default}
        .fb-canvas .react-flow__node-step{cursor:pointer}
        .fb-canvas .react-flow__edge-textbg{rx:6px}
        .fb-canvas .react-flow__attribution{display:none}
        .fb-start-card{transition:transform .15s, box-shadow .15s, border-color .15s}
        .fb-start-card:hover{transform:translateY(-2px);box-shadow:0 10px 28px rgba(20,22,43,.10);border-color:var(--py-border-mid)!important}
        @keyframes fbSheetUp{from{transform:translateY(100%)}to{transform:none}}
      `}</style>

      {/* ── Header ── */}
      <div style={{ display:'flex', alignItems:'center', gap:8, padding:isMobile?'10px 12px':'12px 20px',
        borderBottom:'1px solid var(--py-divider)', flexShrink:0, background:T.surface }}>
        <div style={{ flex:1, minWidth:0, display:'flex', alignItems:'center', gap:8 }}>
          <span style={{ color:T.textFaint, display:'flex', flexShrink:0 }}><Icon.pencil size={15} sw={2}/></span>
          <input value={flowName} onChange={e=>setFlowName(e.target.value)} aria-label={t('Akış adı')}
            style={{ fontSize:isMobile?15:17, fontWeight:800, border:'none', outline:'none', color:T.text, background:'transparent',
              flex:1, minWidth:0, fontFamily:'inherit', textOverflow:'ellipsis' }}/>
        </div>
        {!isNarrow && <>
          <Button variant="ghost" onClick={undo} title={t('Geri al')} style={iconBtn}><Icon.undo size={15} sw={2}/></Button>
          <Button variant="ghost" onClick={redo} title={t('İleri al')} style={iconBtn}><Icon.redo size={15} sw={2}/></Button>
        </>}
        <Button variant="ghost" onClick={()=>setShowStart(true)} title={t('Yeni akış')} style={iconBtn}><Icon.plus size={16} sw={2.2}/></Button>
        <Button variant="ghost" onClick={()=>setShowFlows(true)} title={t('Akışlarım')}
          style={isMobile?iconBtn:{ height:38, padding:'0 14px', flexShrink:0, border:'1px solid var(--py-border-mid)', background:T.surface, color:T.text }}>
          <Icon.folder size={15} sw={1.9}/>{!isMobile && t('Akışlarım')}
        </Button>
        <Button variant="solid" color={GOLD} onClick={saveFlow} disabled={saving}
          style={{ height:38, padding:isMobile?'0 14px':'0 18px', flexShrink:0, whiteSpace:'nowrap', color:'#fff',
            opacity:isValid?1:.55, cursor:saving?'default':'pointer' }}>
          <Icon.play size={13} sw={2.2}/>{saving ? t('Kaydediliyor...') : isMobile ? t('Kaydet') : t('Akışı Kaydet')}
        </Button>
      </div>

      {/* ── Canvas + side panel ── */}
      <div style={{ flex:1, display:'flex', minHeight:0, position:'relative' }}>
        <div className="fb-canvas" style={{ flex:1, minWidth:0, position:'relative', background:T.card }}>
          <ReactFlow
            nodes={rfNodes} edges={rfEdges} nodeTypes={nodeTypes} nodeOrigin={[0.5, 0]}
            nodesDraggable={false} nodesConnectable={false} elementsSelectable={false}
            onNodeClick={(_, n)=>{ if (n.type==='step') selectStep(n.id); }}
            onPaneClick={()=>{ if (!isMobile && panel!=='ai') backToAI(); }}
            fitView fitViewOptions={{ padding:0.25, maxZoom:1 }} minZoom={0.35} maxZoom={1.6}
            proOptions={{ hideAttribution:true }} style={{ background:T.card }}>
            <Background variant={BackgroundVariant.Dots} gap={20} size={1.3} color="var(--py-border-mid)"/>
          </ReactFlow>
          <ZoomBar/>
          {isMobile && !sheetOpen && (
            <button onClick={()=>{ setSelectedId(null); openPanel('ai'); }} style={{ position:'absolute', right:14, bottom:14, zIndex:5,
              display:'flex', alignItems:'center', gap:7, padding:'9px 16px', borderRadius:radius.pill, border:'none', cursor:'pointer',
              background:GOLD, color:'#fff', fontFamily:'inherit', fontSize:13, fontWeight:700, boxShadow:'0 6px 20px rgba(20,22,43,.22)' }}>
              <Icon.sparkle size={15} sw={1.9}/> {t('AI Asistan')}
            </button>
          )}
        </div>

        {!isMobile && (
          <div style={{ width:isTablet?340:420, flexShrink:0, borderLeft:'1px solid var(--py-divider)', background:T.surface,
            display:'flex', flexDirection:'column', minHeight:0 }}>
            {panelBody}
          </div>
        )}

        {isMobile && sheetOpen && <>
          <div onClick={closeSheet} style={{ position:'absolute', inset:0, background:'rgba(15,17,30,.35)', zIndex:20 }}/>
          <div style={{ position:'absolute', left:0, right:0, bottom:0, zIndex:21, height:panel==='ai'?'82%':'72%',
            background:T.surface, borderRadius:'20px 20px 0 0', boxShadow:'0 -8px 30px rgba(20,22,43,.18)',
            display:'flex', flexDirection:'column', minHeight:0, animation:'fbSheetUp .22s ease-out',
            paddingBottom:'env(safe-area-inset-bottom)' }}>
            <div style={{ display:'flex', justifyContent:'center', padding:'8px 0 2px', flexShrink:0 }}>
              <div style={{ width:38, height:4, borderRadius:4, background:'var(--py-border-mid)' }}/>
            </div>
            <div style={{ flex:1, minHeight:0 }}>{panelBody}</div>
          </div>
        </>}
      </div>

      {toast && (
        <div style={{ position:'absolute', left:'50%', transform:'translateX(-50%)', bottom:isMobile?72:24, zIndex:30,
          display:'flex', alignItems:'center', gap:8, padding:'10px 16px', borderRadius:radius.md, maxWidth:'calc(100% - 32px)',
          background:toast.ok?T.greenTint:T.redTint, color:toast.ok?T.greenText:T.redText,
          border:`1px solid ${tint(toast.ok?GREEN:RED,50)}`, fontSize:13, fontWeight:700, boxShadow:shadow.pop }}>
          {toast.ok ? <Icon.check size={14} sw={2.4}/> : <Icon.warningTriangle size={14} sw={2}/>}{toast.text}
        </div>
      )}

      {showStart && (
        <StartModal isMobile={isMobile} hasSaved={hasSaved}
          onClose={()=>setShowStart(false)}
          onScratch={()=>{ setShowStart(false); resetTo(INIT_STEPS, t('Yeni Otomasyon Akışı')); selectStep('start'); }}
          onTemplate={tpl=>{
            setShowStart(false);
            const next = tpl.steps.map(s=>({ ...s, data:{ ...s.data } }));
            resetTo(next, t(tpl.name));
            const firstOpen = next.find(s=>stepStatus(s, next[0].data.platform)!=='ready');
            if (firstOpen) selectStep(firstOpen.id); else { setPanel('ai'); }
          }}
          onAI={startAI}
          onOpenSaved={()=>{ setShowStart(false); setShowFlows(true); }}/>
      )}

      {showFlows && (
        <SavedFlowsPanel clientId={clientId} onClose={()=>setShowFlows(false)}
          onLoad={(nodes, name)=>{ resetTo(normalizeSteps(nodes), name); setPanel('ai'); setSheetOpen(false); }}/>
      )}
    </div>
  );
}

export default function FlowBuilderTab({ config }:{ config:any }) {
  return (
    <ReactFlowProvider>
      <FlowBuilderInner config={config}/>
    </ReactFlowProvider>
  );
}
